import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Eye, Target, Compass, Shield, Heart,
  CheckCircle, TreePine, Activity, Quote, Sunrise, Wind, Brain
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SKILLS = [
  {
    icon: Eye,
    title: 'Visualisation',
    description: 'Spend a few minutes before your round imagining successful shots — the trajectory, the landing, the roll. Visualisation activates the same neural pathways as physical practice, creating a mental blueprint your body can follow.',
    onCourse: 'On the practice green, use one ball for 9 random putts (6-20 feet). Before each putt, simply look at it and ask: what direction and what speed? Build your distance feel through seeing, not mechanics.',
    offCourse: 'Close your eyes for 5 minutes and play the first three holes of your home course in your mind. See each shot, feel each swing, hear the club on the ball. Make it vivid.',
    inApp: 'Log visualisation practice under Pillars of Performance → Mindset. Track whether rounds with pre-round visualisation produce better mental scores.',
  },
  {
    icon: Compass,
    title: 'Intention Setting',
    description: 'Set clear process goals before every round — not score targets, but commitments to HOW you\'ll play. "I will complete my routine on every shot." "I will accept every outcome within 5 steps." These are entirely within your control.',
    onCourse: 'Write your 2-3 process intentions on a card and keep it in your pocket. Glance at it on the 4th tee and the 13th tee as mid-round reset reminders.',
    offCourse: 'After each round, review your intentions honestly. Did you follow through? Rate your adherence out of 10. Process adherence, tracked over time, predicts score improvement.',
    inApp: 'Use session notes to record your pre-round intentions. Tag diary entries with how well you held to them. Your Performance Scorecard "Before the Round" section captures this directly.',
  },
  {
    icon: Shield,
    title: 'Separating Self from Performance',
    description: 'One of the most powerful principles in mental performance coaching: your worth as a person is not determined by your score. Building a "safe place" mentally — where your identity is separate from your results — frees you to take risks and play without fear.',
    onCourse: 'Before the round, remind yourself: "I am not my score today. My job is to follow my process and compete on every shot." This small shift removes the paralysing fear of failure.',
    offCourse: 'Write down why you play golf — not "to shoot low scores" but the deeper reasons: challenge, nature, friendships, personal growth. Return to these when results feel like everything.',
    inApp: 'Your Diary is the ideal place to practise this. After difficult rounds, write about the experience separate from the score. What did you learn? How did you grow?',
  },
  {
    icon: Wind,
    title: 'Breathing & Centring',
    description: 'Deep, intentional breathing is the fastest way to lower cortisol and activate your parasympathetic nervous system. A 60-second breathing exercise before the first tee can be the difference between "ready" and "reactive."',
    onCourse: 'Before stepping onto the first tee: 4 deep breaths. Inhale for 4, hold for 4, exhale for 6. Feel your feet on the ground. You\'re ready.',
    offCourse: 'Practise box breathing (4-4-4-4) for 5 minutes daily. The more automatic it becomes off the course, the more available it is under pressure.',
    inApp: 'Log breathing practice under Pillars of Performance → Mindset. Track your pre-round mood (1-10) in session logs to see how preparation affects your starting state.',
  },
  {
    icon: Sunrise,
    title: 'Warm-Up with Purpose',
    description: 'Most golfers warm up their swing but not their mind. A purposeful warm-up includes both physical preparation and mental priming — building feel, rhythm, and confidence before the first shot counts.',
    onCourse: 'Hit 10-15 range balls focused on FEEL, not mechanics. End with 3 shots to a specific target with your opening tee shot club. Finish on the putting green with the Par 18 game — 9 random putts, scoring like a 9-hole round.',
    offCourse: 'Develop a consistent warm-up checklist: range (feel), short game (touch), putting (eyes), breathing (mind). Same routine every time builds trust.',
    inApp: 'Rate your "Preparation & Warm-Up Quality" in Performance Scorecards. Track whether consistent warm-up routines correlate with better mental scores during the round.',
  },
];

const PragmaticOptimismTooltip = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <span className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[#2C5530] underline decoration-dotted underline-offset-4 cursor-pointer hover:text-[#1E3A21] transition-colors font-['Playfair_Display']"
        data-testid="pragmatic-optimism-trigger"
      >
        pragmatically optimistic
      </button>
      {open && (
        <div className="absolute z-50 left-1/2 -translate-x-1/2 mt-3 w-[340px] sm:w-[400px] bg-white border border-[#E7E5E4] shadow-xl p-5 text-left animate-in fade-in-0 zoom-in-95" data-testid="pragmatic-optimism-tooltip">
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-l border-t border-[#E7E5E4] rotate-45" />
          <p className="font-['Playfair_Display'] text-base text-[#1A1A1A] mb-3">Pragmatic Optimism</p>
          <p className="text-sm text-[#57534E] leading-relaxed mb-4">
            A proactive mindset that combines hopeful, positive thinking with a realistic, evidence-based assessment of current challenges. It focuses on actionable solutions, recognising obstacles while maintaining belief in the ultimate potential to achieve goals. It is "grounded hope" that creates practical, achievable paths forward.
          </p>
          <p className="text-xs text-[#2C5530] font-semibold uppercase tracking-wider mb-2">Key Principles</p>
          <ul className="text-sm text-[#57534E] space-y-1.5 mb-4">
            <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" /><span><strong className="text-[#1A1A1A]">Accepting Reality</strong> — Acknowledging the current situation without hiding behind false optimism.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" /><span><strong className="text-[#1A1A1A]">Controllable Focus</strong> — Directing energy toward actionable steps rather than worrying about uncontrollable factors.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" /><span><strong className="text-[#1A1A1A]">Adaptive Problem-Solving</strong> — Viewing challenges as temporary and solvable, not permanent or personal.</span></li>
            <li className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#2C5530] flex-shrink-0 mt-0.5" /><span><strong className="text-[#1A1A1A]">Hopeful Action</strong> — Maintaining a positive long-term vision while planning for short-term setbacks.</span></li>
          </ul>
          <p className="text-xs text-[#BC562B] font-semibold uppercase tracking-wider mb-2">Compared To</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-red-50 p-2 text-center"><p className="font-semibold text-red-700">Blind Optimist</p><p className="text-red-600 mt-0.5">Ignores risks</p></div>
            <div className="bg-[#2C5530]/10 p-2 text-center"><p className="font-semibold text-[#2C5530]">Pragmatic Optimist</p><p className="text-[#2C5530] mt-0.5">Assesses & solves</p></div>
            <div className="bg-gray-100 p-2 text-center"><p className="font-semibold text-gray-700">Pessimist</p><p className="text-gray-600 mt-0.5">Sees permanent</p></div>
          </div>
        </div>
      )}
    </span>
  );
};

const ApproachBeforePage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="pt-24 pb-20">
        <section className="bg-[#2C5530] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/the-approach" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Approach
            </Link>
            <p className="text-white/50 text-sm uppercase tracking-[0.2em] mb-2">Area One</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">Before the Round</h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Preparation and mental readiness. How you arrive at the course — physically and mentally — sets the tone for everything that follows.
            </p>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">Why Preparation Matters</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                The round begins long before the first tee shot. Your mental state when you arrive at the course — the clarity of your intentions, the quality of your warm-up, and your relationship with the day's expectations — all shape how you perform under pressure.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                Work with tour professionals has revealed that the best performers don't leave preparation to chance. They have <strong>consistent, repeatable mental warm-up routines</strong> that prime their focus and establish the right mindset before the first ball is struck.
              </p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="testimonial">
            <div className="bg-[#F2F0E9] border-l-4 border-[#2C5530] p-6 md:p-8">
              <Quote className="w-8 h-8 text-[#2C5530]/30 mb-4" />
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "The beauty of neutral is it is <PragmaticOptimismTooltip />... I believe one of the bravest things a golfer can do on the course is to stay open to 'possible'."
              </blockquote>
              <p className="text-[#737373] text-sm">— <strong>Karl Morris</strong>, The Mind Factor</p>
            </div>
          </section>

          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Build a pre-round routine that primes your mind for performance.</p>
            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#2C5530] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#BC562B]/5 p-4">
                        <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/the-approach" className="bg-white border border-[#E7E5E4] p-6 hover:shadow-md transition-all group" data-testid="nav-overview">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Overview</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">The Approach</h3>
                <p className="text-sm text-[#57534E]">See all four areas for balanced improvement.</p>
                <ArrowLeft className="w-5 h-5 text-[#2C5530] mt-3" />
              </Link>
              <Link to="/the-approach/during" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/40 hover:shadow-md transition-all group" data-testid="nav-during">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Next Area</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">During the Round</h3>
                <p className="text-sm text-[#57534E]">Pre-shot routines, shot commitment, and staying present.</p>
                <ArrowRight className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApproachBeforePage;
